import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const LowerSidebarManager = () => {
  const { token, user, isSuperAdmin } = useAuth();
  const [customProducts, setCustomProducts] = useState([]);
  const [printifyProducts, setPrintifyProducts] = useState([]);
  const [sidebarConfig, setSidebarConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form for creating custom products
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    price: '',
    store_url: '',
    image_url: '',
    platform: '',
    product_type: 'affiliate', // 'affiliate', 'sold_ad', 'custom'
    is_active: true
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isSuperAdmin) {
      fetchData();
    }
  }, [user, isSuperAdmin]);

  const fetchData = async () => {
    try {
      // Fetch custom products from conscious_products collection (not custom_products)
      const customResponse = await axios.get(`${API}/api/conscious-commerce/products?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomProducts(customResponse.data.products || []);

      // Fetch Printify products  
      const printifyResponse = await axios.get(`${API}/api/printify/products`);
      setPrintifyProducts(printifyResponse.data.products || []);

      // Fetch current sidebar configuration
      const configResponse = await axios.get(`${API}/api/admin/lower-sidebar-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSidebarConfig(configResponse.data.config || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price) || 0
      };

      if (editingProduct) {
        await axios.put(`${API}/api/custom-products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Product updated successfully!');
      } else {
        await axios.post(`${API}/api/custom-products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Product created successfully!');
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setProductForm({
      title: '',
      description: '',
      price: '',
      store_url: '',
      image_url: '',
      platform: '',
      product_type: 'affiliate',
      is_active: true
    });
    setEditingProduct(null);
    setShowCreateForm(false);
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      store_url: product.store_url,
      image_url: product.image_url,
      platform: product.platform || '',
      product_type: product.product_type,
      is_active: product.is_active
    });
    setShowCreateForm(true);
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    
    try {
      await axios.delete(`${API}/api/custom-products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Product deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const updateSidebarConfig = async (newConfig) => {
    try {
      await axios.post(`${API}/api/admin/lower-sidebar-config`, {
        config: newConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSidebarConfig(newConfig);
      alert('✅ Lower sidebar configuration saved successfully!');
    } catch (error) {
      console.error('Error updating sidebar config:', error);
      alert('❌ Error saving sidebar configuration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const addToSidebar = (product, source) => {
    if (sidebarConfig.length >= 11) {
      alert('Maximum 11 items in lower sidebar');
      return;
    }
    
    const newItem = {
      id: product.id,
      source: source, // 'printify' or 'custom'
      title: product.title,
      image_url: product.images?.[0] || product.image_url,
      price: product.price || (product.variants?.[0]?.price ? (product.variants[0].price / 100) : 0),
      store_url: product.custom_store_url || product.store_url
    };
    
    const newConfig = [...sidebarConfig, newItem];
    updateSidebarConfig(newConfig);
  };

  const removeFromSidebar = (itemId) => {
    const newConfig = sidebarConfig.filter(item => item.id !== itemId);
    updateSidebarConfig(newConfig);
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      <h1 className="text-3xl font-bold text-gray-900">🛍️ Lower Sidebar Manager</h1>
      
      {/* Current Lower Sidebar Configuration */}
      <div className="bg-white rounded-lg shadow-lg border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Current Lower Sidebar ({sidebarConfig.length}/11)</h2>
          <button
            onClick={() => updateSidebarConfig(sidebarConfig)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            💾 Save Configuration
          </button>
        </div>
        
        {sidebarConfig.length === 0 ? (
          <p className="text-gray-500">No items configured for lower sidebar</p>
        ) : (
          <div className="space-y-3">
            {sidebarConfig.map((item, index) => (
              <div key={`${item.source}-${item.id}`} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-600">#{index + 1}</span>
                <img src={item.image_url} alt={item.title} className="w-12 h-12 object-cover rounded" />
                <div className="flex-1">
                  <div className="font-medium">{item.title}</div>
                  <div className="text-sm text-gray-600">
                    ${item.price} • {item.source === 'printify' ? 'Printify' : item.platform || 'Custom'}
                  </div>
                </div>
                <button
                  onClick={() => removeFromSidebar(item.id)}
                  className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom Product Creator */}
      <div className="bg-white rounded-lg shadow-lg border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Create Custom Product</h2>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            {showCreateForm ? 'Cancel' : '+ Create Product'}
          </button>
        </div>

        {showCreateForm && (
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Product Title"
                value={productForm.title}
                onChange={(e) => setProductForm({...productForm, title: e.target.value})}
                className="p-3 border rounded-lg"
                required
              />
              <input
                type="number"
                placeholder="Price (USD)"
                value={productForm.price}
                onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                className="p-3 border rounded-lg"
                required
              />
              <input
                type="url"
                placeholder="Store URL"
                value={productForm.store_url}
                onChange={(e) => setProductForm({...productForm, store_url: e.target.value})}
                className="p-3 border rounded-lg"
                required
              />
              <input
                type="url"
                placeholder="Image URL"
                value={productForm.image_url}
                onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                className="p-3 border rounded-lg"
                required
              />
              <input
                type="text"
                placeholder="Platform (e.g., Shopify, Etsy)"
                value={productForm.platform}
                onChange={(e) => setProductForm({...productForm, platform: e.target.value})}
                className="p-3 border rounded-lg"
              />
              <select
                value={productForm.product_type}
                onChange={(e) => setProductForm({...productForm, product_type: e.target.value})}
                className="p-3 border rounded-lg"
              >
                <option value="affiliate">Affiliate Product</option>
                <option value="sold_ad">Sold Advertisement</option>
                <option value="custom">Other Platform Product</option>
              </select>
            </div>
            
            <textarea
              placeholder="Product Description"
              value={productForm.description}
              onChange={(e) => setProductForm({...productForm, description: e.target.value})}
              className="w-full p-3 border rounded-lg"
              rows="3"
            />
            
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              {editingProduct ? 'Update Product' : 'Create Product'}
            </button>
          </form>
        )}
      </div>

      {/* Available Products to Add */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Custom Products */}
        <div className="bg-white rounded-lg shadow-lg border p-6">
          <h2 className="text-xl font-semibold mb-4">Custom Products ({customProducts.length})</h2>
          <div className="space-y-3">
            {customProducts.map((product) => (
              <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img src={product.image_url} alt={product.title} className="w-10 h-10 object-cover rounded" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{product.title}</div>
                  <div className="text-xs text-gray-600">${product.price} • {product.platform}</div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => addToSidebar(product, 'custom')}
                    disabled={sidebarConfig.length >= 11}
                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 disabled:opacity-50"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => editProduct(product)}
                    className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteProduct(product.id)}
                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                  >
                    Del
                  </button>
                </div>
              </div>
            ))}
            {customProducts.length === 0 && (
              <p className="text-gray-500 text-sm">No custom products yet. Create one above.</p>
            )}
          </div>
        </div>

        {/* Available Printify Products */}
        <div className="bg-white rounded-lg shadow-lg border p-6">
          <h2 className="text-xl font-semibold mb-4">Available Printify Products ({printifyProducts.length})</h2>
          <div className="space-y-3">
            {printifyProducts.map((product) => (
              <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img src={product.images?.[0]} alt={product.title} className="w-10 h-10 object-cover rounded" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{product.title}</div>
                  <div className="text-xs text-gray-600">
                    ${(product.variants?.[0]?.price / 100)?.toFixed(2)} • Printify
                  </div>
                </div>
                <button
                  onClick={() => addToSidebar(product, 'printify')}
                  disabled={sidebarConfig.length >= 11}
                  className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LowerSidebarManager;